import React from "react";
import { cellUnpaidPart } from "./vacationDates";

const UNPAID_COLOR = "#8b5cf6";
const VACATION_COLOR = "#5c87b0";

function pad2(value) {
  return String(value).padStart(2, "0");
}

function toDateStr(year, monthIndex, day) {
  return `${year}-${pad2(monthIndex + 1)}-${pad2(day)}`;
}

function isDateInPeriod(dateStr, period) {
  if (!period?.start || !period?.end || !dateStr) return false;
  return dateStr >= period.start && dateStr <= period.end;
}

function unpaidOnHalf(leaves, dateStr, part) {
  return (
    (leaves || []).find((leave) => {
      const cellPart = cellUnpaidPart(leave, dateStr);
      if (!cellPart) return false;
      if (cellPart === "full") return true;
      return cellPart === part;
    }) || null
  );
}

function onKeyActivate(event, fn) {
  if (event.key === "Enter" || event.key === " ") {
    event.preventDefault();
    fn();
  }
}

function HalfCell({
  employee,
  dateStr,
  part,
  existingLeave,
  editVacations,
  onCellClick,
}) {
  const clickable = !editVacations && typeof onCellClick === "function";
  const partLabel = part === "morning" ? "утро" : "вечер";
  const label = `${employee.name}, ${dateStr}, ${partLabel}`;

  const fireClick = () => {
    if (!clickable) return;
    onCellClick({
      employee,
      dateStr,
      part,
      existingLeave: existingLeave || null,
    });
  };

  return (
    <div
      className={`vac-half vac-half--${part}${existingLeave ? " vac-half--unpaid" : ""}`}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      aria-label={label}
      title={existingLeave ? `Отгул ${partLabel}` : undefined}
      onClick={clickable ? fireClick : undefined}
      onKeyDown={clickable ? (event) => onKeyActivate(event, fireClick) : undefined}
      style={existingLeave ? { backgroundColor: UNPAID_COLOR } : undefined}
    />
  );
}

function DayCell({ employee, dateStr, vacation, editVacations, onCellClick }) {
  const leaves = employee.unpaid_leaves || [];
  const morningLeave = unpaidOnHalf(leaves, dateStr, "morning");
  const afternoonLeave = unpaidOnHalf(leaves, dateStr, "afternoon");

  return (
    <div
      className={`vac-cell${vacation ? " vac-cell--vacation" : ""}`}
      data-date={dateStr}
      title={vacation ? `Отпуск ${employee.name} ${dateStr}` : undefined}
    >
      {vacation ? (
        <div
          className="vac-cell-vacation"
          aria-hidden="true"
          style={{ backgroundColor: VACATION_COLOR }}
        />
      ) : (
        <div className="vac-cell-halves">
          <HalfCell
            employee={employee}
            dateStr={dateStr}
            part="morning"
            existingLeave={morningLeave}
            editVacations={editVacations}
            onCellClick={onCellClick}
          />
          <HalfCell
            employee={employee}
            dateStr={dateStr}
            part="afternoon"
            existingLeave={afternoonLeave}
            editVacations={editVacations}
            onCellClick={onCellClick}
          />
        </div>
      )}
    </div>
  );
}

export default function VacationMonthGrid({
  employees = [],
  year,
  monthIndex,
  days = [],
  editVacations = false,
  onCellClick,
}) {
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
  const dayMeta = new Map((days || []).map((day) => [day.date, day]));
  const dayNumbers = Array.from({ length: daysInMonth }, (_, index) => index + 1);

  return (
    <div className="vac-month" style={{ "--vac-days": daysInMonth }}>
      <div className="vac-month-header">
        <div className="vac-month-name">Сотрудник</div>
        {dayNumbers.map((day) => {
          const dateStr = toDateStr(year, monthIndex, day);
          const meta = dayMeta.get(dateStr);
          const kind = meta?.kind;
          const title = meta?.title || undefined;
          return (
            <div
              key={dateStr}
              className={`vac-day${kind ? ` vac-day--${kind}` : ""}`}
              title={title}
              aria-label={title ? `${day}, ${title}` : String(day)}
            >
              {day}
            </div>
          );
        })}
      </div>

      {(employees || []).map((employee) => (
        <div className="vac-month-row" key={employee.employee_id ?? employee.name}>
          <div className="vac-month-name">{employee.name}</div>
          {dayNumbers.map((day) => {
            const dateStr = toDateStr(year, monthIndex, day);
            const vacation = (employee.periods || []).some((period) =>
              isDateInPeriod(dateStr, period)
            );
            return (
              <DayCell
                key={dateStr}
                employee={employee}
                dateStr={dateStr}
                vacation={vacation}
                editVacations={editVacations}
                onCellClick={onCellClick}
              />
            );
          })}
        </div>
      ))}

      <style>{`
        .vac-month {
          overflow-x: auto;
          font-size: 12px;
        }
        .vac-month-header,
        .vac-month-row {
          display: grid;
          grid-template-columns: 140px repeat(var(--vac-days), minmax(22px, 1fr));
          align-items: stretch;
          min-width: 820px;
        }
        .vac-month-header {
          position: sticky;
          top: 0;
          z-index: 2;
          background: var(--bg-secondary);
          border-bottom: 1px solid var(--border-color);
          font-weight: 700;
          color: var(--text-muted);
        }
        .vac-month-row {
          border-bottom: 1px solid var(--border-color);
        }
        .vac-month-name {
          position: sticky;
          left: 0;
          z-index: 1;
          display: flex;
          align-items: center;
          padding: 6px 8px;
          background: var(--bg-card);
        }
        .vac-month-header .vac-month-name {
          background: var(--bg-secondary);
        }
        .vac-day {
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 28px;
          border-left: 1px solid var(--border-color);
        }
        .vac-day--weekend {
          background: rgba(148, 163, 184, 0.18);
        }
        .vac-day--holiday {
          background: rgba(239, 68, 68, 0.18);
          color: #b91c1c;
        }
        .vac-day--preholiday {
          background: rgba(245, 158, 11, 0.16);
        }
        .vac-cell {
          position: relative;
          min-height: 36px;
          border-left: 1px solid var(--border-color);
        }
        .vac-cell-vacation {
          position: absolute;
          inset: 3px 1px;
          border-radius: 3px;
          z-index: 1;
        }
        .vac-cell-halves {
          position: absolute;
          inset: 0;
          display: flex;
          z-index: 2;
        }
        .vac-half {
          flex: 1;
        }
        .vac-half[role="button"] {
          cursor: pointer;
        }
        .vac-half--unpaid {
          opacity: 0.92;
        }
      `}</style>
    </div>
  );
}
